import { useState, type CSSProperties } from "react";
import { Transaction } from "@shared/schema";
import { useColumnResize } from "@/hooks/useColumnResize";
import { getStatusColor, getTransactionTypeColor, formatCurrency, formatDate, getCategoryColor } from "@/lib/utils";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { ArrowUpDown, ArrowUp, ArrowDown, SplitSquareVertical, EyeOff, Store } from "lucide-react";
import aiIconUrl from "@assets/ai-icon.png";

// Parse vendor from description: "UBER *TRIP 1234" → "Uber"
function parseVendor(description: string, merchantName?: string | null): string {
  if (merchantName) return merchantName;
  if (!description) return "Unknown";
  return description
    .replace(/[*#]+/g, " ")
    .replace(/\d{4,}/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .slice(0, 2)
    .join(" ")
    .replace(/^(.)/,  (_, c) => c.toUpperCase()) || "Unknown";
}

// Clean description into two parts: short label + metadata
function parseDescription(description: string): { label: string; meta: string } {
  if (!description) return { label: "—", meta: "" };
  const clean = description.replace(/[*#]+/g, " ").replace(/\s+/g, " ").trim();
  const words = clean.split(" ");
  if (words.length <= 4) return { label: clean, meta: "" };
  return { label: words.slice(0, 4).join(" "), meta: words.slice(4).join(" ") };
}

// Status dot color
function statusDot(status: string | null): { dot: string; text: string; label: string } {
  switch (status) {
    case "approved": return { dot: "bg-green-500", text: "text-green-700", label: "Approved" };
    case "categorized": return { dot: "bg-blue-500", text: "text-blue-700", label: "Categorized" };
    case "rule_applied": return { dot: "bg-indigo-500", text: "text-indigo-700", label: "Rule Applied" };
    case "pending": return { dot: "bg-yellow-500", text: "text-yellow-700", label: "Pending" };
    case "excluded": return { dot: "bg-gray-400", text: "text-gray-500", label: "Excluded" };
    default: return { dot: "bg-gray-400", text: "text-gray-600", label: status?.replace(/_/g, " ") || "Unknown" };
  }
}
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Pencil, Trash, Loader2, Split, AlertTriangle, CheckCircle2 } from "lucide-react";
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TransactionActions } from "@/components/ui/transaction-actions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTransactionCategories } from "@/hooks/use-transactions";
import { Checkbox } from "@/components/ui/checkbox";
import { SplitTransactionDialog } from "@/components/transactions/split-transaction-dialog";
import { TransactionDrawer } from "@/components/transactions/TransactionDrawer";

interface TransactionTableProps {
  transactions: Transaction[];
  onEdit?: (id: number, data: any) => void;
  onDelete?: (id: number) => void;
  onExclude?: (id: number) => void;
  onRestore?: (id: number) => void;
  onSplit?: (transaction: Transaction) => void;
  onSelect?: (id: number) => void;
  selectedTransactions?: number[];
  isLoading?: boolean;
  isPendingEdit?: boolean;
  isPendingDelete?: boolean;
  isPendingExclude?: boolean;
  isPendingRestore?: boolean;
  isPendingSplit?: boolean;
}

export function TransactionTable({
  transactions,
  onEdit,
  onDelete,
  onExclude,
  onRestore,
  onSplit,
  onSelect,
  selectedTransactions = [],
  isLoading,
  isPendingEdit,
  isPendingDelete,
  isPendingExclude,
  isPendingRestore,
  isPendingSplit,
}: TransactionTableProps) {
  const [editTransaction, setEditTransaction] = useState<Transaction | null>(null);
  const [deleteTransaction, setDeleteTransaction] = useState<Transaction | null>(null);
  const [splitTransactionData, setSplitTransactionData] = useState<Transaction | null>(null);
  const [showSplitDialog, setShowSplitDialog] = useState<boolean>(false);
  const [drawerTransaction, setDrawerTransaction] = useState<Transaction | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { categories, isLoading: isCategoriesLoading } = useTransactionCategories();
  const { widths, startResize } = useColumnResize({
    tableId: "transactions",
    defaultWidths: { date: 100, vendor: 140, description: 220, category: 160, amount: 120, status: 110 },
  });
  const resizeHandle = (col: string) => (
    <span
      onMouseDown={startResize(col)}
      className="absolute right-0 top-0 bottom-0 w-[5px] -mr-[2px] z-10 cursor-col-resize group/grip"
    >
      <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border group-hover/grip:w-[3px] group-hover/grip:bg-primary/40 group-hover/grip:rounded-full transition-all" />
    </span>
  );
  const colStyle = (col: string): CSSProperties => ({
    width: widths[col], minWidth: 80, maxWidth: 500, position: "relative" as const,
    borderRight: "1px solid hsl(var(--border) / 0.5)",
  });
  
  // Sorting state
  const [sortField, setSortField] = useState<string | null>("date");
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Transactions are already scoped by business from the API
  const businessTransactions = transactions;
  
  // Sort transactions
  const sortedTransactions = [...businessTransactions].sort((a, b) => {
    if (!sortField) return 0;
    
    const direction = sortDirection === 'asc' ? 1 : -1;
    
    switch(sortField) {
      case 'date':
        return direction * (new Date(a.date).getTime() - new Date(b.date).getTime());
      case 'category':
        const catIdA = (a as any).coaAccountId || (a as any).coa_account_id || a.categoryId;
        const catIdB = (b as any).coaAccountId || (b as any).coa_account_id || b.categoryId;
        const categoryA = catIdA ? categories.find(cat => cat.id === catIdA)?.name || "" : "";
        const categoryB = catIdB ? categories.find(cat => cat.id === catIdB)?.name || "" : "";
        return direction * categoryA.localeCompare(categoryB);
      case 'amount':
        return direction * (parseFloat(a.amount) - parseFloat(b.amount));
      case 'status':
        return direction * (a.status || '').localeCompare(b.status || '');
      default:
        return 0;
    }
  });

  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    categoryId: "",
    date: "",
    type: "",
  });

  const handleEditClick = (transaction: Transaction) => {
    // Defer so the DropdownMenu closes before the Dialog opens (prevents Radix focus trap conflict)
    requestAnimationFrame(() => {
      setEditTransaction(transaction);
      setFormData({
        description: transaction.description,
        amount: transaction.amount.toString(),
        categoryId: transaction.categoryId?.toString() || "",
        date: new Date(transaction.date).toISOString().split("T")[0],
        type: transaction.type,
      });
    });
  };

  const handleEditSubmit = () => {
    if (editTransaction && onEdit) {
      // Check if we're changing the category
      const isChangingCategory = formData.categoryId !== (editTransaction.categoryId?.toString() || "");
      
      // If we're changing the category, also update the transaction status to "categorized"
      onEdit(editTransaction.id, {
        description: formData.description,
        amount: formData.amount,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        date: formData.date,
        type: formData.type,
        // Always set status to categorized when updating the category
        ...(isChangingCategory && { status: "categorized" }),
      });
      
      // Log this change for debugging
      if (isChangingCategory) {
        console.log(`Updated transaction ${editTransaction.id} category to ${formData.categoryId} and status to "categorized"`);
      }
      
      setEditTransaction(null);
    }
  };

  const handleDeleteClick = (transaction: Transaction) => {
    requestAnimationFrame(() => {
      setDeleteTransaction(transaction);
    });
  };

  const handleSplitClick = (transaction: Transaction) => {
    // Defer so the DropdownMenu fully closes before the Dialog opens
    // (prevents Radix aria-hidden focus trap conflict that freezes the UI)
    requestAnimationFrame(() => {
      setSplitTransactionData(transaction);
      setShowSplitDialog(true);
    });
  };
  
  const handleExcludeClick = (transaction: Transaction) => {
    if (onExclude) {
      onExclude(transaction.id);
    }
  };
  
  const handleRestoreClick = (transaction: Transaction) => {
    if (onRestore) {
      onRestore(transaction.id);
    }
  };
  
  const handleSplitDialogOpenChange = (open: boolean) => {
    setShowSplitDialog(open);
    if (!open) {
      setSplitTransactionData(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (deleteTransaction && onDelete) {
      onDelete(deleteTransaction.id);
      setDeleteTransaction(null);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-full h-16 bg-neutral-100 animate-pulse rounded-md" />
        ))}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-6">
        <div className="h-12 w-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-6 w-6 text-green-500" />
        </div>
        <h4 className="text-sm font-semibold text-gray-900 mb-1">You're All Caught Up</h4>
        <p className="text-xs text-gray-500 max-w-xs">No new transactions to review. Everything is clean and up to date.</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop View (md and up) */}
      <div className="hidden md:block overflow-x-auto">
        <Table className="financial-table w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              {onSelect && <TableHead className="w-10"></TableHead>}
              <TableHead style={colStyle("date")} className="text-xs text-muted-foreground font-medium">
                <button className="flex items-center hover:text-foreground transition-colors" onClick={() => handleSort('date')}>
                  Date
                  {sortField === 'date' ? (sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />) : <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />}
                </button>
                {resizeHandle("date")}
              </TableHead>
              <TableHead style={colStyle("vendor")} className="text-xs text-muted-foreground font-medium">
                Vendor
                {resizeHandle("vendor")}
              </TableHead>
              <TableHead style={colStyle("description")} className="text-xs text-muted-foreground font-medium">
                Description
                {resizeHandle("description")}
              </TableHead>
              <TableHead style={colStyle("category")} className="text-xs text-muted-foreground font-medium">
                <button className="flex items-center hover:text-foreground transition-colors" onClick={() => handleSort('category')}>
                  Category
                  {sortField === 'category' ? (sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />) : <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />}
                </button>
                {resizeHandle("category")}
              </TableHead>
              <TableHead style={colStyle("amount")} className="text-xs text-muted-foreground font-medium text-right">
                <button className="flex items-center ml-auto hover:text-foreground transition-colors" onClick={() => handleSort('amount')}>
                  Amount
                  {sortField === 'amount' ? (sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />) : <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />}
                </button>
                {resizeHandle("amount")}
              </TableHead>
              <TableHead style={colStyle("status")} className="text-xs text-muted-foreground font-medium">
                <button className="flex items-center hover:text-foreground transition-colors" onClick={() => handleSort('status')}>
                  Status
                  {sortField === 'status' ? (sortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />) : <ArrowUpDown className="ml-1 h-3 w-3 opacity-40" />}
                </button>
                {resizeHandle("status")}
              </TableHead>
              {(onEdit || onDelete) && <TableHead className="w-12"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTransactions.map((transaction) => {
              // Get appropriate color classes
              const amountColor = getTransactionTypeColor(transaction.type);
              // Resolve category: prefer coaAccountId (COA link), fall back to categoryId
              const txnAny = transaction as any;
              const resolvedCategoryId = txnAny.coaAccountId || txnAny.coa_account_id || transaction.categoryId;
              const categoryId = resolvedCategoryId ?
                (typeof resolvedCategoryId === 'string' ?
                  parseInt(resolvedCategoryId, 10) : resolvedCategoryId) : null;

              const category = categoryId
                ? categories.find(cat => cat.id === categoryId)
                : null;
              
              const categoryColor = getCategoryColor(category?.name);

              const txn = transaction as any;
              const vendor = txn.vendor_name || txn.vendorName || parseVendor(transaction.description, txn.merchantName || txn.merchant_name);
              const { label: descLabel, meta: descMeta } = parseDescription(transaction.description);
              const status = statusDot(transaction.status);
              const openDrawer = () => { setDrawerTransaction(transaction); setDrawerOpen(true); };

              return (
                <TableRow
                  key={transaction.id}
                  className={`${(transaction as any).excluded ? "opacity-50" : ""} hover:bg-muted/30 transition-colors`}
                >
                  {onSelect && (
                    <TableCell className="w-10 pr-0">
                      {(transaction as any).excluded ? (
                        <Checkbox checked={false} disabled={true} />
                      ) : (
                        <Checkbox
                          checked={selectedTransactions.includes(transaction.id)}
                          onCheckedChange={() => onSelect(transaction.id)}
                        />
                      )}
                    </TableCell>
                  )}
                  {/* Date */}
                  <TableCell style={{ width: widths.date }} className="text-xs text-muted-foreground font-mono whitespace-nowrap overflow-hidden">
                    {formatDate(transaction.date)}
                  </TableCell>
                  {/* Vendor */}
                  <TableCell style={{ width: widths.vendor }} className="text-sm overflow-hidden">
                    <button
                      className="text-left font-medium text-foreground hover:text-primary hover:underline cursor-pointer transition-colors truncate block w-full"
                      onClick={openDrawer}
                    >
                      {vendor}
                    </button>
                  </TableCell>
                  {/* Description (2 lines max) */}
                  <TableCell style={{ width: widths.description }} className="text-xs overflow-hidden">
                    <button
                      className="text-left hover:text-primary cursor-pointer transition-colors block w-full"
                      onClick={openDrawer}
                    >
                      <span className="block truncate text-foreground">{descLabel}</span>
                      {descMeta && <span className="block truncate text-muted-foreground text-[11px]">{descMeta}</span>}
                    </button>
                  </TableCell>
                  {/* Category */}
                  <TableCell style={{ width: widths.category }} className="text-xs overflow-hidden">
                    {category ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-muted-foreground text-[11px] truncate max-w-full">
                        {category.name}
                        {(transaction as any).metadata?.aiCategorized && (
                          <img src={aiIconUrl} alt="" className="h-3.5 w-3.5 inline shrink-0" />
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground/50 text-[11px]">Uncategorized</span>
                    )}
                  </TableCell>
                  {/* Amount */}
                  <TableCell style={{ width: widths.amount }} className={`text-sm font-medium font-mono text-right whitespace-nowrap overflow-hidden ${amountColor.text}`}>
                    {transaction.type === "expense" ? "-" : "+"}
                    {formatCurrency(transaction.amount)}
                  </TableCell>
                  {/* Status — dot + text */}
                  <TableCell style={{ width: widths.status }} className="overflow-hidden">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`h-2 w-2 rounded-full shrink-0 ${status.dot}`} />
                      <span className={`text-xs ${status.text} capitalize`}>{status.label}</span>
                    </span>
                  </TableCell>
                  {/* Actions */}
                  {(onEdit || onDelete) && (
                    <TableCell className="w-12">
                      <TransactionActions
                        transaction={transaction}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        onSplit={handleSplitClick}
                        onExclude={handleExcludeClick}
                        onRestore={handleRestoreClick}
                        isPendingEdit={isPendingEdit}
                        isPendingDelete={isPendingDelete}
                        isPendingExclude={isPendingExclude}
                        isPendingRestore={isPendingRestore}
                      />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      
      {/* Mobile View (smaller than md) */}
      <div className="md:hidden">
        <div className="space-y-2">
          {transactions.map((transaction) => {
            const amountColor = getTransactionTypeColor(transaction.type);
            const txnAny = transaction as any;
            const mobileResolvedId = txnAny.coaAccountId || txnAny.coa_account_id || transaction.categoryId;
            const category = mobileResolvedId
              ? categories.find(cat => cat.id === (typeof mobileResolvedId === 'string' ? parseInt(mobileResolvedId, 10) : mobileResolvedId))
              : null;
            const vendor = txnAny.vendor_name || txnAny.vendorName || parseVendor(transaction.description, txnAny.merchantName || txnAny.merchant_name);
            const status = statusDot(transaction.status);
            const openDrawer = () => { setDrawerTransaction(transaction); setDrawerOpen(true); };

            return (
              <div
                key={transaction.id}
                className={`bg-card rounded-lg p-3 border border-border ${txnAny.excluded ? "opacity-50" : ""}`}
              >
                {/* Row 1: vendor + amount */}
                <div className="flex justify-between items-start gap-2 mb-1">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {onSelect && (
                      <Checkbox
                        checked={selectedTransactions.includes(transaction.id)}
                        onCheckedChange={() => onSelect?.(transaction.id)}
                        className="h-4 w-4 shrink-0"
                      />
                    )}
                    <button className="font-medium text-sm truncate text-left hover:text-primary cursor-pointer transition-colors" onClick={openDrawer}>
                      {vendor}
                    </button>
                  </div>
                  <span className={`font-medium ${amountColor.text} font-mono text-sm whitespace-nowrap`}>
                    {transaction.type === "expense" ? "-" : "+"}
                    {formatCurrency(transaction.amount)}
                  </span>
                </div>
                {/* Row 2: description (truncated) + date */}
                <div className="flex justify-between items-center mb-2">
                  <button className="text-xs text-muted-foreground truncate text-left hover:text-primary cursor-pointer transition-colors flex-1 mr-2" onClick={openDrawer}>
                    {transaction.description}
                  </button>
                  <span className="text-[11px] text-muted-foreground font-mono whitespace-nowrap">{formatDate(transaction.date)}</span>
                </div>
                {/* Row 3: category + status */}
                <div className="flex items-center justify-between">
                  <div>
                    {category ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-muted text-muted-foreground text-[11px]">{category.name}</span>
                    ) : (
                      <span className="text-muted-foreground/50 text-[11px]">Uncategorized</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1">
                      <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                      <span className={`text-[11px] ${status.text} capitalize`}>{status.label}</span>
                    </span>
                    {(onEdit || onDelete) && (
                      <TransactionActions
                        transaction={transaction}
                        onEdit={handleEditClick}
                        onSplit={handleSplitClick}
                        onDelete={handleDeleteClick}
                        onExclude={handleExcludeClick}
                        onRestore={handleRestoreClick}
                        isPendingEdit={isPendingEdit}
                        isPendingDelete={isPendingDelete}
                        isPendingExclude={isPendingExclude}
                        isPendingRestore={isPendingRestore}
                        isPendingSplit={isPendingSplit}
                        size="sm"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Edit Transaction Dialog */}
      <Dialog open={!!editTransaction} onOpenChange={(open) => !open && setEditTransaction(null)}>
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm xs:text-base sm:text-lg">Edit Transaction</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 xs:space-y-4 py-2 xs:py-3 sm:py-4">
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 xs:gap-3 sm:gap-4">
              <div className="space-y-1 xs:space-y-2">
                <Label htmlFor="date" className="text-xs xs:text-sm">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm"
                />
              </div>
              <div className="space-y-1 xs:space-y-2">
                <Label htmlFor="type" className="text-xs xs:text-sm">Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData({ ...formData, type: value })}
                >
                  <SelectTrigger id="type" className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income" className="text-xs xs:text-sm">Income</SelectItem>
                    <SelectItem value="expense" className="text-xs xs:text-sm">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1 xs:space-y-2">
              <Label htmlFor="description" className="text-xs xs:text-sm">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm"
              />
            </div>

            <div className="space-y-1 xs:space-y-2">
              <Label htmlFor="amount" className="text-xs xs:text-sm">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm"
              />
            </div>

            <div className="space-y-1 xs:space-y-2">
              <Label htmlFor="category" className="text-xs xs:text-sm">Category</Label>
              <Select
                value={formData.categoryId}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value })}
              >
                <SelectTrigger id="category" className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="text-xs xs:text-sm">
                  <SelectItem value="none" className="text-xs xs:text-sm">Uncategorized</SelectItem>
                  {categories
                    .filter((cat) => cat.type === formData.type)
                    .map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()} className="text-xs xs:text-sm">
                        {category.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="flex flex-col xs:flex-row gap-2 xs:gap-3">
            <DialogClose asChild>
              <Button 
                variant="outline" 
                className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm w-full xs:w-auto"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleEditSubmit} 
              disabled={isPendingEdit}
              className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm w-full xs:w-auto"
            >
              {isPendingEdit ? (
                <>
                  <Loader2 className="mr-1.5 xs:mr-2 h-3 w-3 xs:h-4 xs:w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Transaction Dialog */}
      <Dialog open={!!deleteTransaction} onOpenChange={(open) => !open && setDeleteTransaction(null)}>
        <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm xs:text-base sm:text-lg">Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-2 xs:py-3 sm:py-4">
            <p className="text-xs xs:text-sm sm:text-base">
              Are you sure you want to delete the transaction "{deleteTransaction?.description}"?
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex flex-col xs:flex-row gap-2 xs:gap-3">
            <DialogClose asChild>
              <Button 
                variant="outline"
                className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm w-full xs:w-auto"
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isPendingDelete}
              className="h-8 xs:h-9 sm:h-10 text-xs xs:text-sm w-full xs:w-auto"
            >
              {isPendingDelete ? (
                <>
                  <Loader2 className="mr-1.5 xs:mr-2 h-3 w-3 xs:h-4 xs:w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Transaction"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Transaction Dialog */}
      <SplitTransactionDialog
        transaction={splitTransactionData}
        isOpen={showSplitDialog}
        onOpenChange={handleSplitDialogOpenChange}
        onSuccess={() => {
          // Refresh transactions or update UI after split is saved
          if (onEdit) {
            // If an onEdit is provided, we can use it to trigger a refresh
            onEdit(splitTransactionData?.id || 0, {});
          }
        }}
      />

      {/* Transaction Details Drawer */}
      <TransactionDrawer
        transaction={drawerTransaction}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onEdit={onEdit}
      />
    </>
  );
}
